import { Button, Input, Select, Segmented, Space, Typography, theme } from 'antd';
import { ClearOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { CatalogoResponse } from '@aid/shared';
import { INVOICE_TYPE } from '@aid/shared';
import type { InvoiceType } from '@aid/shared';
import { DateRangeFilter, type DateRangeValue } from '../../../components/DateRangeFilter';
import { ClienteSelect } from '../../../components/ClienteSelect';
import { getFacturaStatusLabel } from '../lib/factura-table-helpers';
import type { FacturaTableGroupBy, FacturasTranslate } from './factura-table-types';
import { FACTURA_TABLE_GROUP } from './factura-table-types';

const { Text } = Typography;

export interface FacturasTableToolbarProps {
  t: FacturasTranslate;
  showClientFilter: boolean;
  showGrouping: boolean;
  showCreate: boolean;
  groupBy: FacturaTableGroupBy;
  idCliente: string;
  search: string;
  tipoFactura?: InvoiceType;
  idEstado?: number;
  fechaDesde?: string;
  fechaHasta?: string;
  estadosCobro: CatalogoResponse[];
  canCreate: boolean;
  resultTotal: number;
  hasFilters: boolean;
  onGroupByChange: (value: FacturaTableGroupBy) => void;
  onClientChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onTipoFacturaChange: (value: InvoiceType | undefined) => void;
  onEstadoChange: (value: number | undefined) => void;
  onDateRangeChange: (value: DateRangeValue | null) => void;
  onClear: () => void;
  onCreate: () => void;
}

export function FacturasTableToolbar({
  t,
  showClientFilter,
  showGrouping,
  showCreate,
  groupBy,
  idCliente,
  search,
  tipoFactura,
  idEstado,
  fechaDesde,
  fechaHasta,
  estadosCobro,
  canCreate,
  resultTotal,
  hasFilters,
  onGroupByChange,
  onClientChange,
  onSearchChange,
  onTipoFacturaChange,
  onEstadoChange,
  onDateRangeChange,
  onClear,
  onCreate,
}: FacturasTableToolbarProps) {
  const { token } = theme.useToken();
  const dateRange: DateRangeValue | null =
    fechaDesde !== undefined || fechaHasta !== undefined
      ? [fechaDesde ?? null, fechaHasta ?? null]
      : null;
  const clienteId =
    idCliente !== '' && Number.isFinite(Number(idCliente)) ? Number(idCliente) : undefined;

  return (
    <div
      style={{
        marginBottom: 16,
        display: 'grid',
        gap: 12,
      }}
    >
      <div
        role="group"
        aria-label={t('pages.cobros.filterArea')}
        className="table-filters"
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Input
          prefix={<SearchOutlined />}
          aria-label={t('pages.cobros.searchPlaceholder')}
          placeholder={t('pages.cobros.searchPlaceholder')}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          allowClear
          style={{ width: 300, maxWidth: '100%' }}
        />
        {showClientFilter && (
          <div style={{ width: 220, maxWidth: '100%' }}>
            <ClienteSelect
              value={clienteId}
              onChange={(id) => onClientChange(id === undefined ? '' : String(id))}
              placeholder={t('pages.cobros.clientFilterPlaceholder')}
            />
          </div>
        )}
        <Select<number>
          aria-label={t('pages.cobros.filtroEstado')}
          placeholder={t('pages.cobros.filtroEstado')}
          allowClear
          style={{ width: 180 }}
          value={idEstado}
          onChange={onEstadoChange}
          options={estadosCobro.map((estado) => ({
            label: getFacturaStatusLabel(estado.key, t),
            value: estado.id,
          }))}
        />
        <Select<InvoiceType>
          aria-label={t('pages.cobros.filtroTipoFactura')}
          placeholder={t('pages.cobros.filtroTipoFactura')}
          allowClear
          style={{ width: 180 }}
          value={tipoFactura}
          onChange={(value) => onTipoFacturaChange(value as InvoiceType | undefined)}
          options={[
            {
              label: t('pages.cobros.tipoFacturaPeriodica'),
              value: INVOICE_TYPE.PERIODICA,
            },
            {
              label: t('pages.cobros.tipoFacturaExtraordinaria'),
              value: INVOICE_TYPE.EXTRAORDINARIA,
            },
          ]}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            maxWidth: '100%',
          }}
        >
          <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
            {t('pages.cobros.fechaEmision')}
          </Text>
          <DateRangeFilter
            value={dateRange}
            onChange={onDateRangeChange}
            style={{ width: 300, maxWidth: '100%' }}
          />
        </div>
        <Button
          type="link"
          icon={<ClearOutlined />}
          aria-label={t('common.clear')}
          onClick={onClear}
          disabled={!hasFilters}
        >
          {t('common.clear')}
        </Button>
      </div>
      {showGrouping && (
        <div
          role="group"
          aria-label={t('pages.cobros.groupingArea')}
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            paddingTop: 12,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <Text type="secondary">{t('common.groupBy')}</Text>
          <Segmented
            aria-label={t('common.groupBy')}
            options={[
              { label: t('common.groupByNone'), value: FACTURA_TABLE_GROUP.NONE },
              { label: t('common.groupByCliente'), value: FACTURA_TABLE_GROUP.CLIENTE },
              { label: t('common.groupByEstado'), value: FACTURA_TABLE_GROUP.ESTADO },
            ]}
            value={groupBy}
            onChange={(value) => onGroupByChange(value as FacturaTableGroupBy)}
          />
          <Text type="secondary">{t('pages.cobros.resultCount', { count: resultTotal })}</Text>
          {groupBy !== FACTURA_TABLE_GROUP.NONE && (
            <Text type="secondary">{t('pages.cobros.groupingPageNote')}</Text>
          )}
        </div>
      )}
      {showCreate && canCreate && (
        <Space wrap size={[8, 8]} style={{ justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            aria-label={t('pages.cobros.nuevaFactura')}
            onClick={onCreate}
          >
            {t('pages.cobros.nuevaFactura')}
          </Button>
        </Space>
      )}
    </div>
  );
}
